import { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Typography, Card, Dropdown, Alert, Select, Tag, App, DatePicker, Space, Statistic, Checkbox } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MoreOutlined, BarChartOutlined, CopyOutlined, RollbackOutlined, DownloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import teamsService, { type Team, type CreateTeamRequest, type UpdateTeamRequest, type UsageReportEntry } from '../../api/teamsService';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;

/** Format update_datetime - handles BigQuery format { value: string } or plain ISO string. Shows UTC, 24h. */
function formatUpdateDatetime(val: string | { value?: string } | null | undefined): string {
  const str = !val ? null : typeof val === 'string' ? val : (val as { value?: string })?.value;
  if (!str) return '-';
  const date = new Date(str);
  return isNaN(date.getTime()) ? '-' : `${date.toLocaleString(undefined, { timeZone: 'UTC', hour12: false })} UTC`;
}

/** Extract timestamp for sorting - handles BigQuery format */
function getTimestampForSort(val: string | { value?: string } | null | undefined): number {
  const str = !val ? null : typeof val === 'string' ? val : (val as { value?: string })?.value;
  if (!str) return 0;
  const date = new Date(str);
  return isNaN(date.getTime()) ? 0 : date.getTime();
}

function escapeCSV(val: unknown): string {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

interface TeamFormValues {
  team_id: string;
  display_name: string;
  tio_account_id: string;
  api_key: string;
  admin_emails: string[];
}

export const TeamsManagement = () => {
  const { message, modal } = App.useApp();
  const { authState } = useAuth();
  const token = authState.token;
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [form] = Form.useForm<TeamFormValues>();
  
  // Usage report modal state
  const [isUsageReportModalOpen, setIsUsageReportModalOpen] = useState(false);
  const [usageReportLoading, setUsageReportLoading] = useState(false);
  const [usageReportData, setUsageReportData] = useState<UsageReportEntry[]>([]);
  const [usageReportForm] = Form.useForm();

  // Include soft-deleted teams in the list (for management UI)
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Fetch all teams
  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const data = await teamsService.getAllTeams(includeDeleted);
      setTeams(data);
    } catch (error) {
      message.error('Failed to fetch teams');
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  }, [message, includeDeleted]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // Handle create/edit modal (don't allow editing deleted teams)
  const handleOpenModal = (team?: Team) => {
    if (team?.deleted) return;
    if (team) {
      setEditingTeam(team);
      form.setFieldsValue(team);
    } else {
      setEditingTeam(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTeam(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!token) {
        message.error('Authentication required');
        return;
      }

      setSaving(true);

      if (editingTeam) {
        // Update existing team
        const updates: UpdateTeamRequest = {
          team_id: values.team_id,
          display_name: values.display_name,
          tio_account_id: values.tio_account_id,
          api_key: values.api_key,
          admin_emails: values.admin_emails || [],
        };

        await teamsService.updateTeam(editingTeam.team_id, updates, token);
        message.success('Team updated successfully');
      } else {
        // Create new team
        const teamData: CreateTeamRequest = {
          team_id: values.team_id,
          display_name: values.display_name,
          tio_account_id: values.tio_account_id,
          api_key: values.api_key,
          admin_emails: values.admin_emails || [],
        };

        await teamsService.createTeam(teamData, token);
        message.success('Team created successfully');
      }

      handleCloseModal();
      fetchTeams();
    } catch (error) {
      console.error('Error saving team:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save team';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (teamId: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await teamsService.deleteTeam(teamId, token);
      message.success('Team soft-deleted successfully');
      fetchTeams();
    } catch (error) {
      message.error('Failed to delete team');
      console.error('Error deleting team:', error);
    }
  };

  const handleRestore = async (teamId: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await teamsService.setTeamDeleted(teamId, false, token);
      message.success('Team restored successfully');
      fetchTeams();
    } catch (error) {
      message.error('Failed to restore team');
      console.error('Error restoring team:', error);
    }
  };

  // Usage report handlers
  const handleOpenUsageReportModal = () => {
    // Set default date range to last 7 days
    const endDate = dayjs().utc();
    const startDate = endDate.subtract(7, 'day');
    usageReportForm.setFieldsValue({
      dateRange: [startDate, endDate],
    });
    setIsUsageReportModalOpen(true);
  };

  const handleCloseUsageReportModal = () => {
    setIsUsageReportModalOpen(false);
    setUsageReportData([]);
    usageReportForm.resetFields();
  };

  const handleFetchUsageReport = async () => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      const values = await usageReportForm.validateFields();
      const [startDate, endDate] = values.dateRange as [Dayjs, Dayjs];

      if (!startDate || !endDate) {
        message.error('Please select a date range');
        return;
      }

      // Convert to ISO timestamp strings (UTC)
      const startDateISO = startDate.utc().toISOString();
      const endDateISO = endDate.utc().toISOString();

      setUsageReportLoading(true);
      const report = await teamsService.getUsageReport(startDateISO, endDateISO, token);
      setUsageReportData(report.data || []);
      message.success(`Usage report loaded for ${report.count} teams`);
    } catch (error) {
      console.error('Error fetching usage report:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch usage report';
      message.error(errorMessage);
    } finally {
      setUsageReportLoading(false);
    }
  };

  const handleCopyAdminEmails = async () => {
    try {
      // Collect all admin emails from all teams
      const allEmails = teams
        .flatMap(team => team.admin_emails || [])
        .filter((email, index, self) => self.indexOf(email) === index) // Remove duplicates
        .filter(email => email.trim() !== ''); // Remove empty strings

      if (allEmails.length === 0) {
        message.warning('No admin emails found in any team');
        return;
      }

      // Format as comma-separated for email clients
      const emailsText = allEmails.join(', ');

      // Copy to clipboard
      await navigator.clipboard.writeText(emailsText);
      message.success(`Copied ${allEmails.length} admin email${allEmails.length !== 1 ? 's' : ''} to clipboard`);
    } catch (error) {
      console.error('Error copying emails:', error);
      message.error('Failed to copy emails to clipboard');
    }
  };

  const handleExportTeamsCSV = () => {
    if (teams.length === 0) {
      message.warning('No teams to export');
      return;
    }
    const headers = [
      'team_id',
      'display_name',
      'tio_account_id',
      'api_key',
      'admin_emails',
      'updated_by',
      'update_datetime',
      'deleted',
    ];
    const csvRows = [headers.map(escapeCSV).join(',')];
    for (const t of teams) {
      const vals = [
        t.team_id,
        t.display_name,
        t.tio_account_id,
        t.api_key ?? '',
        (t.admin_emails || []).join('; '),
        t.updated_by ?? '',
        formatUpdateDatetime(t.update_datetime),
        t.deleted ? 'yes' : 'no',
      ];
      csvRows.push(vals.map(escapeCSV).join(','));
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `teams-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('Teams exported to CSV');
  };

  const usageReportColumns: ColumnsType<UsageReportEntry> = [
    {
      title: 'Team',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) => a.display_name.localeCompare(b.display_name),
      render: (name: string) => (
        <Text style={{ fontSize: 12 }}>{name}</Text>
      ),
    },
    {
      title: 'Account ID',
      dataIndex: 'tio_account_id',
      key: 'tio_account_id',
      sorter: (a, b) => a.tio_account_id.localeCompare(b.tio_account_id),
      render: (id: string) => (
        <a
          href={`https://admintool.cs.tomorrow.io/accounts?accountId=${id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 12 }}
        >
          {id}
        </a>
      ),
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions_count',
      key: 'sessions_count',
      sorter: (a, b) => a.sessions_count - b.sessions_count,
      render: (count: number) => (
        <Text style={{ fontSize: 12, fontWeight: 500 }}>{count.toLocaleString()}</Text>
      ),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Users',
      dataIndex: 'users_count',
      key: 'users_count',
      sorter: (a, b) => a.users_count - b.users_count,
      render: (count: number) => (
        <Text style={{ fontSize: 12, fontWeight: 500 }}>{count.toLocaleString()}</Text>
      ),
    },
  ];

  const columns: ColumnsType<Team> = [
    {
      title: 'Team ID',
      dataIndex: 'team_id',
      key: 'team_id',
      sorter: (a, b) => a.team_id.localeCompare(b.team_id),
      render: (id: string, record) => (
        <Space size={4}>
          <Text code style={{ fontSize: 12 }}>{id}</Text>
          {record.deleted && <Tag color="red">Deleted</Tag>}
        </Space>
      ),
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) => a.display_name.localeCompare(b.display_name),
      render: (name: string) => (
        <Text style={{ fontSize: 12 }}>{name}</Text>
      ),
    },
    {
      title: 'Account ID',
      dataIndex: 'tio_account_id',
      key: 'tio_account_id',
      sorter: (a, b) => a.tio_account_id.localeCompare(b.tio_account_id),
      render: (id: string) => (
        <a
          href={`https://admintool.cs.tomorrow.io/accounts?accountId=${id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 12 }}
        >
          {id}
        </a>
      ),
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      render: (key: string) => (
        <Text style={{ fontSize: 12 }} code>
          {key ? `${key.substring(0, 8)}...` : '-'}
        </Text>
      ),
    },
    {
      title: 'Admin Emails',
      dataIndex: 'admin_emails',
      key: 'admin_emails',
      render: (emails: string[] | null) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {emails && emails.length > 0 ? (
            emails.map((email) => (
              <Tag key={email} style={{ fontSize: 11, margin: 0 }}>
                {email}
              </Tag>
            ))
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Updated',
      key: 'update_info',
      width: 180,
      sorter: (a, b) => getTimestampForSort(a.update_datetime) - getTimestampForSort(b.update_datetime),
      defaultSortOrder: 'descend',
      render: (_, record) => (
        <Space orientation="vertical" size={0} style={{ fontSize: 11 }}>
          {record.updated_by && (
            <Text type="secondary" style={{ fontSize: 11 }}>{record.updated_by}</Text>
          )}
          <Text type="secondary" style={{ fontSize: 10 }}>
            {formatUpdateDatetime(record.update_datetime)}
          </Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          ...(record.deleted
            ? [
                {
                  key: 'restore',
                  icon: <RollbackOutlined />,
                  label: 'Restore',
                  onClick: () => handleRestore(record.team_id),
                },
              ]
            : [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: 'Edit',
                  onClick: () => handleOpenModal(record),
                },
                { type: 'divider' as const },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: 'Delete',
                  danger: true,
                  onClick: () => {
                    modal.confirm({
                      title: 'Soft-delete Team',
                      content: (
                        <div>
                          Are you sure you want to soft-delete <Text code>{record.team_id}</Text>?
                          <br />
                          <Typography.Text type="secondary">The team can be restored later from the list.</Typography.Text>
                        </div>
                      ),
                      okText: 'Yes, Delete',
                      cancelText: 'Cancel',
                      okButtonProps: { danger: true },
                      onOk: () => handleDelete(record.team_id),
                    });
                  },
                },
              ]),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
      <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Title level={2} style={{ margin: 0 }}>Teams Management</Title>
          <Space>
            <Checkbox
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            >
              Include deleted
            </Checkbox>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyAdminEmails}
            >
              Copy Admin Emails
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportTeamsCSV}
            >
              Export CSV
            </Button>
            <Button
              icon={<BarChartOutlined />}
              onClick={handleOpenUsageReportModal}
            >
              Usage Report
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              Add Team
            </Button>
          </Space>
        </div>

        <Alert
          title="Teams Configuration"
          description={
            <span>
              Manage teams and their associated Tomorrow.io accounts. Teams are used to organize users
              and filter feedback by team. Each team maps to a Tomorrow.io account ID and API key.
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
        />

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={teams}
            rowKey="team_id"
            loading={loading}
            pagination={false}
            scroll={{ y: 'calc(100vh - 280px)' }}
            size="small"
            footer={() => (
              <div style={{ textAlign: 'right', color: '#8c8c8c', fontSize: 12 }}>
                {teams.length} team{teams.length !== 1 ? 's' : ''}
              </div>
            )}
          />
        </div>
      </Card>

      {/* Modal */}
      <Modal
        title={editingTeam ? 'Edit Team' : 'Add Team'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        confirmLoading={saving}
        width={600}
        okText={editingTeam ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: '24px' }}
        >
          <Form.Item
            name="team_id"
            label="Team ID"
            rules={[
              { required: true, message: 'Please enter team ID' },
              { pattern: /^[a-z0-9_-]+$/, message: 'Only lowercase letters, numbers, hyphens and underscores allowed' },
            ]}
          >
            <Input placeholder="e.g., ferrari, mercedes, redbull" />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="Display Name"
            rules={[{ required: true, message: 'Please enter display name' }]}
          >
            <Input placeholder="e.g., Ferrari, Mercedes-AMG Petronas" />
          </Form.Item>

          <Form.Item
            name="tio_account_id"
            label="Tomorrow.io Account ID"
            rules={[{ required: true, message: 'Please enter Tomorrow.io account ID' }]}
          >
            <Input placeholder="e.g., 695f7682363690b6503eaaf0" />
          </Form.Item>

          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: true, message: 'Please enter API key' }]}
          >
            <Input.Password placeholder="Enter the API key for this account" />
          </Form.Item>

          <Form.Item
            name="admin_emails"
            label="Admin Emails"
            tooltip="Email addresses of team administrators. Press Enter after each email."
            rules={[
              {
                validator: async (_, emails) => {
                  if (emails && emails.length > 0) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const invalidEmails = emails.filter((email: string) => !emailRegex.test(email));
                    if (invalidEmails.length > 0) {
                      throw new Error(`Invalid email(s): ${invalidEmails.join(', ')}`);
                    }
                  }
                },
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder="Enter email addresses and press Enter"
              tokenSeparators={[',', ' ']}
              style={{ width: '100%' }}
              notFoundContent={null}
            />
          </Form.Item>
          </Form>
        </Modal>

      {/* Usage Report Modal */}
      <Modal
        title="Team Usage Report"
        open={isUsageReportModalOpen}
        onCancel={handleCloseUsageReportModal}
        width={900}
        footer={[
          <Button key="cancel" onClick={handleCloseUsageReportModal}>
            Close
          </Button>,
          <Button
            key="fetch"
            type="primary"
            onClick={handleFetchUsageReport}
            loading={usageReportLoading}
          >
            Fetch Report
          </Button>,
        ]}
      >
        <Form
          form={usageReportForm}
          layout="vertical"
          style={{ marginBottom: 24 }}
        >
          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[{ required: true, message: 'Please select a date range' }]}
            tooltip="Select the date range for session usage statistics"
          >
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
              placeholder={['Start Date', 'End Date']}
            />
          </Form.Item>
        </Form>

        {usageReportData.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Space size="large">
              <Statistic
                title="Total Teams"
                value={usageReportData.length}
              />
              <Statistic
                title="Total Sessions"
                value={usageReportData.reduce((sum, entry) => sum + entry.sessions_count, 0).toLocaleString()}
              />
              <Statistic
                title="Total Users"
                value={usageReportData.reduce((sum, entry) => sum + entry.users_count, 0).toLocaleString()}
              />
            </Space>
          </div>
        )}

        {usageReportData.length > 0 && (
          <Table
            columns={usageReportColumns}
            dataSource={usageReportData}
            rowKey="tio_account_id"
            loading={usageReportLoading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} teams`,
            }}
            size="small"
            scroll={{ y: 400 }}
          />
        )}

        {usageReportData.length === 0 && !usageReportLoading && (
          <Alert
            title="No Data"
            description="Select a date range and click 'Fetch Report' to load usage statistics."
            type="info"
            showIcon
          />
        )}
      </Modal>
      </div>
    );
  };

export default TeamsManagement;
